import React, { useEffect, useState } from 'react';
import { Crown } from 'lucide-react';
import { getEmployeePhotoBlob } from '../../api';

const CONTRACT_COLORS = {
  CDI:           'bg-green-100 text-green-700',
  CDD:           'bg-orange-100 text-orange-700',
  apprentissage: 'bg-blue-100 text-blue-700',
};

function calcAnciennete(startDate) {
  if (!startDate) return null;
  const start = new Date(startDate);
  const now   = new Date();
  let years  = now.getFullYear() - start.getFullYear();
  let months = now.getMonth()    - start.getMonth();
  if (months < 0) { years--; months += 12; }
  if (years > 0 && months > 0) return `${years} an${years > 1 ? 's' : ''} ${months} m.`;
  if (years > 0) return `${years} an${years > 1 ? 's' : ''}`;
  if (months > 0) return `${months} mois`;
  return '< 1 mois';
}

const AVATAR_COLORS = [
  '#14b8a6', '#8b5cf6', '#f97316', '#06b6d4',
  '#ec4899', '#84cc16', '#f59e0b', '#3b82f6',
];
function avatarColor(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function EmployeeAvatar({ employee, size = 56 }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [imgDims, setImgDims] = useState(null);

  useEffect(() => {
    if (!employee.photoUrl) return;
    let cancelled = false;
    let url;
    getEmployeePhotoBlob(employee.id)
      .then(res => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        const probe = new Image();
        probe.onload = () => {
          if (cancelled) return;
          const ar = probe.naturalWidth / probe.naturalHeight;
          let w, h, ml = 0, mt = 0;
          if (ar >= 1) {
            // Photo plus large que haute : on cale sur la hauteur, on rogne les côtés
            h = size; w = size * ar; ml = -(w - size) / 2;
          } else {
            // Photo plus haute que large : on cale sur la largeur, on rogne haut/bas
            w = size; h = size / ar; mt = -(h - size) / 2;
          }
          setImgDims({ w, h, ml, mt });
          setBlobUrl(url);
        };
        probe.onerror = () => { if (!cancelled) setBlobUrl(null); };
        probe.src = url;
      })
      .catch(() => { if (!cancelled) setBlobUrl(null); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [employee.id, employee.photoUrl, size]);

  const initials = `${employee.firstName?.[0] ?? ''}${employee.lastName?.[0] ?? ''}`.toUpperCase();
  const color    = avatarColor(`${employee.firstName}${employee.lastName}`);

  if (blobUrl && imgDims) return (
    <div
      style={{
        width: size,
        height: size,
        overflow: 'hidden',
        borderRadius: '50%',
        outline: `2.5px solid ${color}55`,
        outlineOffset: '2px',
      }}
      className="border-2 border-white shadow-sm flex-shrink-0"
    >
      <img
        src={blobUrl}
        alt=""
        style={{
          width: imgDims.w,
          height: imgDims.h,
          marginLeft: imgDims.ml,
          marginTop: imgDims.mt,
          display: 'block',
        }}
      />
    </div>
  );

  // Initiales en SVG : centrage parfait via dominantBaseline=central,
  // immune au line-height/cap-height et géré nativement par html2canvas.
  const gradId = `grad-${employee.id}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ flexShrink: 0, display: 'block', overflow: 'visible' }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={`${color}cc`} />
          <stop offset="100%" stopColor={color} />
        </linearGradient>
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2 - 2}
        fill={`url(#${gradId})`}
        stroke="white"
        strokeWidth="2"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2}
        fill="none"
        stroke={`${color}55`}
        strokeWidth="2.5"
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        fill="white"
        fontSize={size * 0.36}
        fontWeight="700"
        fontFamily="inherit"
      >
        {initials}
      </text>
    </svg>
  );
}

export default function OrgChartCard({ employee, mode = 'screen', onClick, roleName, isSecondary = false }) {
  const anciennete  = calcAnciennete(employee.startDate);
  const contractCls = CONTRACT_COLORS[employee.contractType] || 'bg-gray-100 text-gray-600';
  const roleLabel   = roleName || employee.role || '';
  const isPdf       = mode === 'pdf';
  const showPosition = employee.position &&
    employee.position.toLowerCase().trim() !== roleLabel.toLowerCase().trim();

  return (
    <div
      onClick={onClick}
      data-testid="org-card"
      data-emp-id={employee.id}
      title={isSecondary ? `Structure principale : ${employee.structureName || '—'}` : undefined}
      style={isPdf ? { wordSpacing: '3px' } : undefined}
      className={`
        relative z-[1] bg-white rounded-xl
        flex flex-col items-center text-center gap-2
        select-none mx-auto w-44 px-3 pt-4 pb-3
        ${isSecondary
          ? 'border-2 border-dashed border-primary/50 bg-primary/[0.02] shadow-sm'
          : 'border border-gray-200 shadow-sm hover:border-primary/30 hover:shadow-md'}
        ${onClick ? 'cursor-pointer hover:-translate-y-0.5 transition-all duration-200' : ''}
      `}
    >
      {/* Badge "S" — structure secondaire */}
      {isSecondary && (
        <span
          className="absolute top-1.5 right-1.5 bg-primary text-white font-bold rounded-full shadow flex items-center justify-center"
          style={{ width: 18, height: 18, fontSize: 9, lineHeight: '18px', textAlign: 'center' }}
        >
          S
        </span>
      )}

      {/* Couronne continuité direction */}
      {employee.isContinuiteDirection && (
        <span
          className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-100 text-amber-500 border border-amber-200 rounded-full px-1.5 py-0.5 shadow-sm flex items-center gap-0.5"
          title="Continuité direction"
        >
          <Crown size={9} />
        </span>
      )}

      <EmployeeAvatar employee={employee} size={56} />

      <div className="w-full">
        <p className={`font-bold text-gray-900 leading-tight text-[13px] ${isPdf ? 'whitespace-normal break-words' : 'truncate'}`}>
          {employee.firstName} {employee.lastName}
        </p>
        {roleLabel && (
          <p className={`text-primary font-semibold mt-0.5 leading-tight text-[11px] ${isPdf ? 'whitespace-normal' : 'truncate'}`}>
            {roleLabel}
          </p>
        )}
        {showPosition && (
          <p className={`text-gray-400 mt-0.5 leading-tight text-[10px] ${isPdf ? 'whitespace-normal break-words' : 'truncate'}`}>
            {employee.position}
          </p>
        )}
      </div>

      {!isPdf && (anciennete || employee.contractType) && (
        <div className="flex items-center justify-center gap-1.5 w-full flex-wrap">
          {anciennete && (
            <span className="text-[10px] text-gray-400">{anciennete}</span>
          )}
          {employee.contractType && (
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full leading-tight ${contractCls}`}>
              {employee.contractType}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
